import React, { useState } from 'react';
import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    InputLabel,
    Stack,
    TextField
} from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import 'yup-phone-lite';
import { errorMessage, userRoles, userTypes } from 'utils/constants';
import CountryCodeSelect from '../common/CountryCodeSelect';
import SnackbarNotify from 'utils/snackbar/SnackbarNotify';

const CONTACT_REQUIRED = 'Enter an email address, a phone number, or both';

const validationSchema = Yup.object().shape({
    first_name: Yup.string().trim().required(errorMessage.firstName.required),
    last_name: Yup.string().trim().required(errorMessage.lastName.required),
    email: Yup.string().trim().email(errorMessage.email.valid)
});

const validateContact = values => {
    const { number, code, country } = values.phone;

    if (!number) {
        return values.email.trim() ? {} : { email: CONTACT_REQUIRED };
    }

    try {
        Yup.string().phone(country).validateSync(`${code}${number}`);
        return {};
    } catch (error) {
        return { phone: { number: errorMessage.phone.valid } };
    }
};

const InviteAssociateModal = ({ onClose, onSubmit }) => {
    const [selectedCountry, setSelectedCountry] = useState(null);

    const initialValues = {
        first_name: '',
        last_name: '',
        email: '',
        phone: {
            code: 1,
            country: 'US',
            number: ''
        }
    };

    const handleSubmit = async values => {
        const email = values.email.trim().toLowerCase();

        const payload = {
            first_name: values.first_name.trim().toLowerCase(),
            last_name: values.last_name.trim().toLowerCase(),
            ...(email && { email }),
            ...(values.phone.number && {
                phone: {
                    number: Number.parseInt(values.phone.number),
                    code: Number.parseInt(values.phone.code),
                    country: values.phone.country.trim()
                }
            }),
            role: userRoles.ASSOCIATE,
            type: userTypes.MEMBER
        };

        try {
            await onSubmit(payload);
            SnackbarNotify.success('Invite sent — pending acceptance.');
            onClose();
        } catch (error) {
            console.error('Error inviting associate:', error);
            SnackbarNotify.error(
                `Error inviting associate: ${error?.message || 'Unexpected error'}`
            );
        }
    };

    return (
        <>
            <DialogTitle>Invite Associate</DialogTitle>
            <Divider />
            <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validate={validateContact}
                validationSchema={validationSchema}
                validateOnChange
            >
                {({
                    errors,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    isSubmitting,
                    setFieldValue,
                    touched,
                    values
                }) => (
                    <>
                        <DialogContent sx={{ p: 2.5, maxHeight: '70vh' }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>First Name</InputLabel>
                                        <TextField
                                            required
                                            id="associateFirstName"
                                            name="first_name"
                                            placeholder="First Name"
                                            fullWidth
                                            autoComplete="given-name"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={
                                                touched.first_name && !!errors.first_name
                                            }
                                            helperText={
                                                touched.first_name && errors.first_name
                                            }
                                            value={values.first_name}
                                            inputProps={{
                                                style: { textTransform: 'capitalize' }
                                            }}
                                            FormHelperTextProps={{ sx: { mx: 0 } }}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>Last Name</InputLabel>
                                        <TextField
                                            required
                                            id="associateLastName"
                                            name="last_name"
                                            placeholder="Last Name"
                                            fullWidth
                                            autoComplete="family-name"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={
                                                touched.last_name && !!errors.last_name
                                            }
                                            helperText={
                                                touched.last_name && errors.last_name
                                            }
                                            value={values.last_name}
                                            inputProps={{
                                                style: { textTransform: 'capitalize' }
                                            }}
                                            FormHelperTextProps={{ sx: { mx: 0 } }}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>Email</InputLabel>
                                        <TextField
                                            id="associateEmail"
                                            name="email"
                                            type="email"
                                            placeholder="Email"
                                            fullWidth
                                            autoComplete="email"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.email && !!errors.email}
                                            helperText={touched.email && errors.email}
                                            value={values.email}
                                            FormHelperTextProps={{ sx: { mx: 0 } }}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Stack spacing={0.5}>
                                        <InputLabel htmlFor="phone.country">
                                            Country Code
                                        </InputLabel>
                                        <CountryCodeSelect
                                            label=""
                                            size="medium"
                                            value={selectedCountry}
                                            defaultRegion={values.phone?.country || 'US'}
                                            onChange={newCountry => {
                                                if (!newCountry) return;

                                                setSelectedCountry(newCountry);
                                                setFieldValue(
                                                    'phone.code',
                                                    String(newCountry.code).replace(
                                                        /\D/g,
                                                        ''
                                                    )
                                                );
                                                setFieldValue(
                                                    'phone.country',
                                                    newCountry.region
                                                );
                                            }}
                                            sx={{ width: '100%' }}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={8}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>Phone Number</InputLabel>
                                        <TextField
                                            id="associatePhoneNumber"
                                            name="phone.number"
                                            placeholder="Phone Number"
                                            inputMode="numeric"
                                            onChange={e =>
                                                setFieldValue(
                                                    'phone.number',
                                                    e.target.value.replace(/\D/g, '')
                                                )
                                            }
                                            fullWidth
                                            onBlur={handleBlur}
                                            value={values.phone?.number}
                                            error={
                                                touched.phone?.number &&
                                                !!errors.phone?.number
                                            }
                                            helperText={
                                                touched.phone?.number &&
                                                errors.phone?.number
                                            }
                                            FormHelperTextProps={{ sx: { mx: 0 } }}
                                        />
                                    </Stack>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <Divider />
                        <DialogActions sx={{ p: 2.5 }}>
                            <Button color="error" onClick={onClose}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                            >
                                Invite Associate
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Formik>
        </>
    );
};

export default InviteAssociateModal;
